import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AccessType, StoryElement } from "@quintype/components";
import { StoryTags } from "../../story-page-components/story-tags.js";
import { StoryPageCard } from "../../story-page-components/story-page-card.js";
import { AdService } from "../../ad-service.js";
import { addTargetBlankToExtLinks } from "../../../../utils/add-target-blank-to-extlinks";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../../sso-components/sso-utils";
import { Author } from "../../basic/author.js";
import { StoryHeadline } from "../../atoms/story-headline/index.js";
import { StorySubheadline } from "../../atoms/story-subheadline/index.js";
import { StorySection } from "../../atoms/story-section/index.js";
import { StoryFooter } from "../../molecules/story-footer";
import { Share } from "../../molecules/share/index.js";
import { getSocialShareStyle, selectedStoryTemplate, accessCheck } from "../../../utils.js";
import { Sidebar } from "../../molecules/sidebar";
import { AmazonPolly } from "../../amazon-polly/amazon-polly";
import "./video.m.css";

function getVideoStoryClass(videoStory) {
  switch (videoStory) {
    case "hero-priority":
      return "hero-priority-temp";
    case "headline-priority":
      return "headline-priority-temp";
    default:
      return "";
  }
}

function getTemplate(videoStory) {
  switch (videoStory) {
    case "hero-priority":
      return "grid-4-12";
    case "headline-priority":
      return "grid-3-10";
    default:
      return "grid-2-10";
  }
}

function getStoryCardTemplate(cards = [], story, config, firstVideo, firstStory) {
  const videoStory = selectedStoryTemplate("video-story", story, config);
  const cdnImage = get(config, ["cdn-image"], "");
  const hasMultiCards = cards.length > 1;
  const infiniteScrollStyle = get(config, ["theme-attributes", "infiniteScrollStyle"]);
  const firstStoryCard = get(story, ["cards", 0]);

  if (infiniteScrollStyle === "read-more" && !firstStory) {
    const { id: firstVideoId = -1 } = firstVideo;
    return (
      <div key={firstStoryCard.id} className={`blank-story__card--with-ad ${getTemplate(videoStory)}`}>
        <StoryPageCard
          firstVideoElementID={firstVideoId}
          key={firstStoryCard.id}
          card={firstStoryCard}
          config={config}
          story={story}
          firstStory={firstStory}
          cdn={cdnImage}
          clazzName="grid-column-full"
        />
      </div>
    );
  }
  return cards.map((card, index) => {
    const { id: firstVideoId = -1 } = firstVideo;
    if (index === 0 && hasMultiCards) {
      return (
        <div key={card.id} className={`blank-story__card--with-ad ${getTemplate(videoStory)}`}>
          <StoryPageCard
            firstVideoElementID={firstVideoId}
            key={card.id}
            card={card}
            config={config}
            story={story}
            firstStory={firstStory}
            cdn={cdnImage}
            clazzName="grid-column-full"
          />
          <div className="app-ad app-ad--story-horizontal grid-column-full">
            <AdService adtype="Story-Middle-Ad" slotId="Story-Middle-Horizontal-Video" />
          </div>
        </div>
      );
    } else {
      return (
        <div className={`${getTemplate(videoStory)}`}>
          <StoryPageCard
            firstVideoElementID={firstVideoId}
            key={card.id}
            card={card}
            config={config}
            story={story}
            firstStory={firstStory}
            cdn={cdnImage}
            clazzName="grid-column-full"
          />
        </div>
      );
    }
  });
}

function VideoStoryTemplate({ config = {}, story = {}, access: storyAccess = {}, accessLoading, preview, firstStory }) {
  const videoStory = selectedStoryTemplate("video-story", story, config);
  const isStoryPublic = checkIfStoryPublic(story);
  const {
    cards = [],
    "last-published-at": date,
    "first-published-at": firstPublishedTime,
    authors: authorsArray = [
      {
        name: story["author-name"],
        id: story["author-id"]
      }
    ],
    id: storyId
  } = story;
  const publishedTime = date || firstPublishedTime;
  const socialShareStyle = getSocialShareStyle(config);
  const showPolly = get(storyAccess, [storyId, "granted"], false);
  const infiniteScrollStyle = get(config, ["theme-attributes", "infiniteScrollStyle"], "default");
  const firstVideo =
    cards
      .reduce((acc, currEle) => {
        acc = acc.concat(currEle["story-elements"]);
        return acc;
      }, [])
      .find(
        ({ type, subtype }) => type === "youtube-video" || (type === "jsembed" && subtype === "dailymotion-video")
      ) || {};

  const HeaderVideo = props => {
    return (
      <div styleName="header-videos" data-testid="header-video">
        <StoryElement element={firstVideo} key={firstVideo.id} story={props.story} />
      </div>
    );
  };
  const videoStoryList = { default: false, "hero-priority": true, "headline-priority": true };
  const isHorizontal = videoStoryList[videoStory] && socialShareStyle.includes("horizontal");
  function getSocialShare(videoStory) {
    switch (videoStory) {
      case "hero-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "headline-priority":
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} vertical />;
      default:
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} />;
    }
  }

  function getSideAd(videoStory) {
    switch (videoStory) {
      case "hero-priority":
        return <Sidebar story={story} template={videoStory} showAd={false} />;
      case "headline-priority":
        return <Sidebar story={story} template={videoStory} showAd={true} wide />;
      default:
        return (
          <div className="app-ad app-ad--story-mrec">
            <AdService adtype="Story-Mrec" slotId="Story-Mrec-Video" />
          </div>
        );
    }
  }
  // section
  const supportSectionGrid = {
    default: "grid-col-2-10 ",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-1-2"
  };
  const gridSectionStyle = supportSectionGrid[videoStory]
    ? supportSectionGrid[videoStory]
    : supportSectionGrid["default"];

  // Headline
  const supportHeadlineGrid = {
    default: "grid-col-2-10 ",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-2-3"
  };
  const gridHeadlineStyle = supportHeadlineGrid[videoStory]
    ? supportHeadlineGrid[videoStory]
    : supportHeadlineGrid["default"];

  // Subheadline
  const supportSubHeadlineGrid = {
    default: "grid-col-2-10 ",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-2-10 grid-row-3-4"
  };
  const gridSubHeadlineStyle = supportSubHeadlineGrid[videoStory]
    ? supportSubHeadlineGrid[videoStory]
    : supportSubHeadlineGrid["default"];

  // Amazon Polly
  const supportAmazonPollyGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12 grid-row-5-6",
    "headline-priority": "grid-col-2-10 grid-row-4-5"
  };

  const gridAmazonPollyStyle = supportAmazonPollyGrid[videoStory]
    ? supportAmazonPollyGrid[videoStory]
    : supportAmazonPollyGrid["default"];

  // Video
  const supportVideoGrid = {
    default: "grid-col-2-14",
    "hero-priority": "grid-col-2-14 grid-row-1-2",
    "headline-priority": "grid-col-2-10 grid-row-5-6"
  };
  const gridVideoStyle = supportVideoGrid[videoStory] ? supportVideoGrid[videoStory] : supportVideoGrid["default"];

  // Horizontal SocialShare Author
  const supportHorizontalAuthorGrid = {
    default: "grid-col-2-6",
    "hero-priority": "grid-col-4-8 grid-row-6-7",
    "headline-priority": "grid-col-3-7 grid-row-6-7"
  };
  const gridHorizontalAuthorStyle = supportHorizontalAuthorGrid[videoStory]
    ? supportHorizontalAuthorGrid[videoStory]
    : supportHorizontalAuthorGrid["default"];

  // Vertical SocialShare Author
  const supportVerticalAuthorGrid = {
    default: "grid-col-2-6",
    "hero-priority": "grid-col-4-8 grid-row-6-7",
    "headline-priority": "grid-col-3-10 grid-row-6-7"
  };
  const gridVerticalAuthorStyle = supportVerticalAuthorGrid[videoStory]
    ? supportVerticalAuthorGrid[videoStory]
    : supportVerticalAuthorGrid["default"];

  // Horizontal SocialShare
  const supportHorizontalSocialShareGrid = {
    default: "grid-col-6-10",
    "hero-priority": "grid-col-8-12 grid-row-6-7",
    "headline-priority": "grid-col-2-3 grid-row-6-auto"
  };
  const gridHorizotalSocialShareStyle = supportHorizontalSocialShareGrid[videoStory]
    ? supportHorizontalSocialShareGrid[videoStory]
    : supportHorizontalSocialShareGrid["default"];

  // Vertical SocialShare
  const supportVerticalSocialShareGrid = {
    default: "grid-col-6-10",
    "hero-priority": "grid-col-3-4 grid-row-2-auto",
    "headline-priority": "grid-col-2-3 grid-row-6-auto"
  };
  const gridVerticalSocialShareStyle = supportVerticalSocialShareGrid[videoStory]
    ? supportVerticalSocialShareGrid[videoStory]
    : supportVerticalSocialShareGrid["default"];

  // horizontal line
  const supportHorizontalLineGrid = {
    default: "grid-col-2-10"
  };
  const gridHorizontalLineStyle = supportHorizontalLineGrid[videoStory]
    ? supportHorizontalLineGrid[videoStory]
    : supportHorizontalLineGrid["default"];

  // Related Stories and SideAd
  const supportRelatedStoriesGrid = {
    default: "grid-col-10-14 grid-row-3-9",
    "hero-priority": "grid-col-2-14",
    "headline-priority": "grid-col-10-14 grid-row-1-13"
  };
  const gridRelatedStoriesStyle = supportRelatedStoriesGrid[videoStory]
    ? supportRelatedStoriesGrid[videoStory]
    : supportRelatedStoriesGrid["default"];

  // Social Tags
  const supportSocialTagsGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-3-10"
  };
  const gridSocialTagsStyle = supportSocialTagsGrid[videoStory]
    ? supportSocialTagsGrid[videoStory]
    : supportSocialTagsGrid["default"];

  // //MeType
  const supportMetypeGrid = {
    default: "grid-col-2-10",
    "hero-priority": "grid-col-4-12",
    "headline-priority": "grid-col-3-10"
  };
  const gridMetypeStyle = supportMetypeGrid[videoStory] ? supportMetypeGrid[videoStory] : supportMetypeGrid["default"];
  const readTime = get(story, ["read-time"]);
  return (
    <article
      className="blank-story grid"
      styleName={`video-story ${getVideoStoryClass(videoStory)} ${isHorizontal ? "horizontal" : ""}`}
    >
      {/* first  video */}
      <div data-testid="header-video" styleName="video" className={gridVideoStyle}>
        <HeaderVideo story={story} />
      </div>
      {/* section name */}
      <span styleName="story-grid-item" className={gridSectionStyle}>
        <StorySection story={story} config={config} />
      </span>
      {/* headline */}
      <h1 styleName="story-grid-item" className={gridHeadlineStyle}>
        <StoryHeadline headline={story.headline} />
      </h1>
      {/* subheadline */}
      <p styleName="story-grid-item subheadline" className={gridSubHeadlineStyle}>
        <StorySubheadline subheadline={story.subheadline} />
      </p>
      {/* Amazon Polly */}
      {showPolly && (
        <div styleName="story-grid-item amazon-polly-wrapper" className={gridAmazonPollyStyle}>
          <AmazonPolly story={story} />
        </div>
      )}
      {/* Author */}
      <div
        data-testid="author-name"
        styleName="story-grid-item author"
        className={`${isHorizontal ? gridHorizontalAuthorStyle : gridVerticalAuthorStyle}`}
      >
        <Author config={config} authors={authorsArray} isLink showDateOnly date={publishedTime} readTime={readTime} />
      </div>
      {/* Social-share */}
      <div
        data-testid="social-share"
        styleName="story-grid-item social-share"
        className={`${isHorizontal ? gridHorizotalSocialShareStyle : gridVerticalSocialShareStyle}`}
      >
        <div styleName="sticky">{getSocialShare(videoStory)}</div>
      </div>
      {/* Horizontal Line */}
      <div styleName="horizontal-line" className={gridHorizontalLineStyle} />

      {/* story cards */}
      {isStoryPublic
        ? getStoryCardTemplate(cards, story, config, firstVideo, firstStory)
        : handleAccess(story, storyAccess, accessLoading, config, cards =>
            getStoryCardTemplate(cards, story, config, firstVideo, firstStory)
          )}

      {(infiniteScrollStyle !== "read-more" || (infiniteScrollStyle === "read-more" && firstStory)) && (
        <>
          {/* tag */}
          <div styleName="story-grid-item " className={gridSocialTagsStyle}>
            <StoryTags tags={story.tags} config={config} />
          </div>
          {/* sidebar and side ad */}
          <div styleName="related-story" className={gridRelatedStoriesStyle}>
            {getSideAd(videoStory)}
          </div>
          {/* metype */}
          <div styleName="story-grid-item" className={gridMetypeStyle}>
            {!preview && <StoryFooter story={story} />}
          </div>
        </>
      )}
    </article>
  );
}

VideoStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  preview: PropTypes.bool,
  firstStory: PropTypes.bool,
  access: PropTypes.object,
  accessLoading: PropTypes.bool
};

class VideoStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  render() {
    return (
      <div className="story-grid" ref={el => (this.storyWrapperElement = el)}>
        <VideoStoryTemplate {...this.props} />
      </div>
    );
  }
}

VideoStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class VideoStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"], false);

    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <VideoStoryAccessWrapper
            accessIsLoading={accessIsLoading}
            accessUpdated={accessUpdated}
            initAccessType={initAccessType}
            checkAccess={checkAccess}
            {...this.props}
          />
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const VideoStory = connect(mapStateToProps)(VideoStoryBase);
